import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { createPost, updatePost, deletePost } from '@/lib/firebase/posts';
import { uploadImage, uploadVideo } from '@/lib/firebase/storage';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import ImageUploadModal from './ImageUploadModal';
import PublishingOptions from './PublishingOptions';
import ConfirmDialog from '@/components/ui/confirm-dialog';
import { generatePost } from '@/services/ai';
import { sendInvites as sendInviteEmails } from '@/lib/email/service';
import { characterCount } from '@/lib/utils';
// import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { getUserData } from '@/lib/firebase/user';
import { getInviteCountForUser } from '@/lib/firebase/invites';

type AccessType = 'paid_members' | 'public' | 'members';

interface PublishingOptionsType {
  access: AccessType;
  price: string;
  earlyAccess: boolean;
  releaseDate: Date | null;
}

interface Post {
  id: string;
  userId: string;
  title: string;
  content: string;
  type: 'text' | 'video' | 'image';
  videoUrl?: string;
  images?: string[];
  publishingOptions?: PublishingOptionsType;
  createdAt: any;
  updatedAt?: any;
  stats?: {
    likes: number;
    comments: number;
    impressions: number;
  };
  isLocked?: boolean;
  isPinned?: boolean;
  likedBy?: string[];
  likes?: string[];
}

interface CreatePostFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialPost?: Post;
  onPostSaved?: (post: Post | null) => void;
}

// interface RichTextEditorProps {
//   content: string;
//   onChange: (content: string) => void;
//   onImageUpload?: (file: File) => Promise<void>;
//   images?: string[];
//   onImageRemove?: (index: number) => void;
// }

// interface PublishingOptionsProps {
//   options: PublishingOptionsType;
//   onChange: (options: PublishingOptionsType) => void;
// }

export default function CreatePostForm({
  isOpen,
  onClose,
  initialPost,
  onPostSaved
}: CreatePostFormProps) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoUpload, setVideoUpload] = useState('');
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const generatingDialogRef = useRef<HTMLDialogElement>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const howItWorksDialogRef = useRef<HTMLDialogElement>(null);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
  const generateConfirmDialogRef = useRef<HTMLDialogElement>(null);
  const [showUnlocked, setShowUnlocked] = useState(false);
  const unlockedDialogRef = useRef<HTMLDialogElement>(null);
  const [styleExample, setStyleExample] = useState<string>('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const inviteDialogRef = useRef<HTMLDialogElement>(null);
  const [inviteEmails, setInviteEmails] = useState(['', '', '']);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteCount, setInviteCount] = useState<number>(0);
  const mainDialogRef = useRef<HTMLDialogElement>(null);
  const [publishingOptions, setPublishingOptions] = useState<PublishingOptionsType>({
    access: 'paid_members',
    price: '',
    earlyAccess: false,
    releaseDate: null,
  });

  // Load user style preferences
  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      try {
        const data = await getUserData(user.uid);
        const prefs = (data as any)?.aiPreferences;
        if (prefs?.styleExample) setStyleExample(prefs.styleExample);
      } catch { }
    })();
  }, [user?.uid]);

  // Load invite count
  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const n = await getInviteCountForUser(user.uid);
      setInviteCount(n);
    })();
  }, [user?.uid, showInviteModal]);

  // Set initial values when dialog opens with a post
  useEffect(() => {
    if (isOpen && initialPost) {
      setTitle(initialPost.title);
      setContent(initialPost.content);
      setVideoUrl(initialPost.videoUrl || '');
      setImages(initialPost.images || []);
      setVideoUpload((initialPost as any).videoUpload || '');
      setPublishingOptions(initialPost.publishingOptions || {
        access: 'paid_members',
        price: '',
        earlyAccess: false,
        releaseDate: null,
      });
    }
  }, [isOpen, initialPost]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setContent('');
      setVideoUrl('');
      setImages([]);
      setVideoUpload('');
      setPublishingOptions({
        access: 'paid_members',
        price: '',
        earlyAccess: false,
        releaseDate: null,
      });
      setStep(1);
    }
  }, [isOpen]);

  // Native <dialog>/showModal(): top-layer stacking so this can never render
  // behind a fixed-position sibling (e.g. the cookie consent banner) regardless
  // of z-index, matching the LaunchPopup/VisitorIntentPrompt precedent (TSF-114/115).
  useEffect(() => {
    if (isOpen) mainDialogRef.current?.showModal();
    else mainDialogRef.current?.close();
  }, [isOpen]);

  useEffect(() => {
    if (showHowItWorks) howItWorksDialogRef.current?.showModal();
    else howItWorksDialogRef.current?.close();
  }, [showHowItWorks]);

  useEffect(() => {
    if (showGenerateConfirm) generateConfirmDialogRef.current?.showModal();
    else generateConfirmDialogRef.current?.close();
  }, [showGenerateConfirm]);

  useEffect(() => {
    if (showInviteModal) inviteDialogRef.current?.showModal();
    else inviteDialogRef.current?.close();
  }, [showInviteModal]);

  useEffect(() => {
    if (showUnlocked) unlockedDialogRef.current?.showModal();
    else unlockedDialogRef.current?.close();
  }, [showUnlocked]);

  useEffect(() => {
    if (isGenerating) generatingDialogRef.current?.showModal();
    else generatingDialogRef.current?.close();
  }, [isGenerating]);



  const handleNext = () => {
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!user) return;

    // Block if over 2000 chars
    if (characterCount(content) > 2000) {
      toast.error('Content exceeds 2000 characters. Please shorten it.');
      return;
    }

    setIsSubmitting(true);
    try {
      const type = (videoUpload || videoUrl) ? 'video' as const : images.length > 0 ? 'image' as const : 'text' as const;
      const postData = {
        title,
        content,
        type,
        videoUrl: videoUrl || '',
        videoUpload: videoUpload || '',
        images: images || [],
        publishingOptions: {
          access: publishingOptions.access || 'paid_members',
          price: publishingOptions.price || '',
          earlyAccess: publishingOptions.earlyAccess || false,
          releaseDate: publishingOptions.releaseDate || null,
        },
        isLocked: publishingOptions.access === 'paid_members' || publishingOptions.access === 'members',
      };

      let savedPost: Post;
      if (initialPost?.id) {
        // Update existing post
        await updatePost(initialPost.id, {
          title: postData.title,
          content: postData.content,
          type: postData.type,
          videoUrl: postData.videoUrl,
          videoUpload: postData.videoUpload,
          images: postData.images,
          publishingOptions: postData.publishingOptions,
          isLocked: postData.isLocked,
        });
        savedPost = {
          ...initialPost,
          ...postData,
          updatedAt: new Date(),
        };
      } else {
        // Create new post
        const newPostId = await createPost(user.uid, postData);
        if (!newPostId) {
          throw new Error('Failed to create post: No post ID returned');
        }
        savedPost = {
          id: newPostId,
          userId: user.uid,
          ...postData,
          createdAt: new Date(),
          updatedAt: new Date(),
          stats: {
            likes: 0,
            comments: 0,
            impressions: 0,
          },
          likes: [],
          likedBy: [],
        };
      }

      // Call onPostSaved with the saved post
      if (onPostSaved) {
        onPostSaved(savedPost);
      }

      toast.success(initialPost ? 'Post updated successfully' : 'Post created successfully');
      onClose();

      // Reset form
      setTitle('');
      setContent('');
      setVideoUrl('');
      setImages([]);
      setStep(1);
    } catch (error) {
      console.error('[CreatePostForm] Error saving post:', error);
      toast.error('Failed to save post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const url = await uploadImage(file, `posts/${user?.uid}`);
      setImages(prev => [...prev, url]);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      const message = typeof error?.message === 'string' && error.message.includes('5MB')
        ? 'Image is too large. Max file size is 5MB.'
        : 'Failed to upload image';
      toast.error(message);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async () => {
    if (!initialPost) return;

    try {
      await deletePost(initialPost.id);
      toast.success('Post deleted successfully');
      onClose();
      // Pass null to indicate post deletion
      onPostSaved?.(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
    setIsDeleteDialogOpen(false);
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
    <dialog
      ref={mainDialogRef}
      onCancel={() => onClose()}
      onClick={(e) => { if (e.target === mainDialogRef.current) onClose(); }}
      className="m-auto max-w-2xl w-[calc(100%-2rem)] rounded-lg p-0 bg-transparent backdrop:bg-black/50"
    >
      <div className="bg-white rounded-lg w-full">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold">
            {initialPost ? 'Edit post' : 'Create text post'}
          </h2>
          <button onClick={onClose}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {step === 1 ? (
          <div className="p-4 space-y-4">
            <input
              type="text"
              placeholder="Add a title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-2xl font-bold placeholder-gray-400 focus:outline-none"
            />

            <RichTextEditor
              content={content}
              onChange={(newContent) => {
                console.log('[CreatePostForm] Content changed:', newContent);
                setContent(newContent);
              }}
              onImageUpload={handleImageUpload}
              images={images}
              onImageRemove={removeImage}
              maxCharacters={2000}
            />

            {/* Content length */}
            <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
              <span>Max 2000 characters</span>
              <span>{characterCount(content)} / 2000</span>
            </div>

            <div className="mt-6 p-3 rounded-md border border-dashed">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="font-medium">Create text with AI</p>
                  <p className="text-sm text-gray-600">Add your draft above, then generate a polished post. This will replace current content.</p>
                  {inviteCount < 3 && (
                    <p className="text-xs text-blue-700">Invite {3 - inviteCount} more to unlock AI.</p>
                  )}
                  <button
                    type="button"
                    className="text-sm text-primary hover:underline"
                    onClick={() => setShowHowItWorks(true)}
                  >
                    How it works
                  </button>
                </div>
                <button
                  type="button"
                  className="px-3 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                  onClick={() => {
                    if (!characterCount(content)) {
                      setShowHowItWorks(true);
                      return;
                    }
                    if (inviteCount < 3) {
                      setShowInviteModal(true);
                    } else {
                      setShowGenerateConfirm(true);
                    }
                  }}
                  disabled={isGenerating}
                >
                  {isGenerating ? 'Generating…' : 'Generate with AI'}
                </button>
              </div>
            </div>

            {/* Images and Video blocks with extra spacing */}
            {/* Images */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images (optional)
              </label>
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  className="text-sm text-primary hover:text-primary/90 disabled:opacity-60"
                  disabled={isUploadingVideo || !user?.uid}
                  onClick={async () => {
                    try {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = async () => {
                        const file = input.files?.[0];
                        if (!file || !user?.uid) return;
                        try {
                          const url = await uploadImage(file, `posts/${user.uid}`);
                          setImages(prev => [...prev, url]);
                          toast.success('Image uploaded successfully');
                        } catch {
                          toast.error('Failed to upload image');
                        }
                      };
                      input.click();
                    } catch { }
                  }}
                >
                  Upload image
                </button>
              </div>
              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {images.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={img} alt={`Upload ${i + 1}`} className="h-24 w-24 rounded-lg object-cover" />
                      <button type="button" className="absolute -right-2 -top-2 rounded-full bg-white p-1 text-gray-500 shadow-sm hover:text-gray-700" onClick={() => removeImage(i)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Video */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video (optional)
              </label>
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  className="text-sm text-primary hover:text-primary/90 disabled:opacity-60"
                  disabled={isUploadingVideo || !user?.uid}
                  onClick={async () => {
                    try {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'video/*';
                      input.onchange = async () => {
                        const file = input.files?.[0];
                        if (!file || !user?.uid) return;
                        setIsUploadingVideo(true);
                        setVideoProgress(0);
                        try {
                          const url = await uploadVideo(file, `posts/${user.uid}`, (p) => setVideoProgress(p));
                          setVideoUpload(url);
                          toast.success('Video uploaded');
                        } catch {
                          toast.error('Failed to upload video');
                        } finally {
                          setIsUploadingVideo(false);
                        }
                      };
                      input.click();
                    } catch { }
                  }}
                >
                  {isUploadingVideo ? `Uploading ${videoProgress}%` : 'Upload video'}
                </button>
              </div>
              {videoUpload && (
                <div className="mb-3">
                  <div className="aspect-video rounded-lg overflow-hidden bg-black/5">
                    <video className="w-full h-full" src={videoUpload} controls />
                  </div>
                  <div className="mt-2 flex items-center justify-end">
                    <button className="text-xs text-red-600" onClick={() => setVideoUpload('')}>Remove</button>
                  </div>
                </div>
              )}
              <input
                type="url"
                placeholder="YouTube URL (optional)"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <PublishingOptions
              options={publishingOptions}
              onChange={setPublishingOptions}
            />
          </div>
        )}

        <div className="flex justify-between items-center p-4 border-t">
          {step === 2 ? (
            <button
              onClick={handleBack}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Back
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          )}
          <div className="flex items-center space-x-4">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600">
              <span>View post as member</span>
            </button>
            {initialPost?.id && (
              <button
                onClick={() => setIsDeleteDialogOpen(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            )}
            <button
              onClick={step === 1 ? handleNext : handleSubmit}
              disabled={isSubmitting || !title || !content || characterCount(content) > 2000}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {step === 1 ? 'Next' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      <ImageUploadModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onUpload={handleImageUpload}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Post"
        description="Are you sure you want to delete this post? This action cannot be undone."
      />
    </dialog>

      {/* The modals below are rendered as siblings of the main dialog above, not
          nested inside its JSX -- HTML top-layer dialogs don't compose cleanly when
          DOM-nested (each showModal() call promotes independently to the top layer,
          so visual nesting in JSX would be misleading). They still function as
          "on top of" the main dialog because each is promoted to the top layer only
          while open, stacking above the main dialog in open order. */}

      {/* How it works modal */}
      <dialog
        ref={howItWorksDialogRef}
        onCancel={() => setShowHowItWorks(false)}
        onClick={(e) => { if (e.target === howItWorksDialogRef.current) setShowHowItWorks(false); }}
        className="m-auto max-w-lg w-[calc(100%-2rem)] rounded-lg p-0 bg-transparent backdrop:bg-black/50"
      >
          <div className="bg-white rounded-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold">Create text with AI</h3>
            <p>You can generate your post quickly using AI. Add quick notes (bullets or a rough draft) in the content field above, then click <strong>Generate with AI</strong> to create the text.</p>
            <p>You can optionally customize your AI style: go to <strong>Profile → Customize your AI</strong> to set your writing style so it reflects your personality and communication style.</p>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2" onClick={() => setShowHowItWorks(false)}>Close</button>
            </div>
          </div>
      </dialog>

      {/* Generate confirmation modal (appears after invites are sent) */}
      <dialog
        ref={generateConfirmDialogRef}
        onCancel={() => setShowGenerateConfirm(false)}
        onClick={(e) => { if (e.target === generateConfirmDialogRef.current) setShowGenerateConfirm(false); }}
        className="m-auto max-w-lg w-[calc(100%-2rem)] rounded-lg p-0 bg-transparent backdrop:bg-black/50"
      >
          <div className="bg-white rounded-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold">Generate with AI</h3>
            <p>Have you already added the draft text based on which the post will be generated? If not, close this and add the text first.</p>
            <p className="text-sm text-gray-600">Note: generating content with AI will replace the previously added content.</p>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2" onClick={() => setShowGenerateConfirm(false)}>Cancel</button>
              <button
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                disabled={isGenerating}
                onClick={async () => {
                  try {
                    setIsGenerating(true);
                    setShowGenerateConfirm(false);
                    const result = await generatePost({ draftText: content, title, styleExample });
                    setContent(result.content);
                  } catch (err: any) {
                    console.error('AI generation failed', err);
                    toast.error(err.message || 'Failed to generate post');
                  } finally {
                    setIsGenerating(false);
                  }
                }}
              >
                {isGenerating ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
      </dialog>

      {/* Invite gating modal */}
      <dialog
        ref={inviteDialogRef}
        onCancel={() => setShowInviteModal(false)}
        onClick={(e) => { if (e.target === inviteDialogRef.current) setShowInviteModal(false); }}
        className="m-auto max-w-lg w-[calc(100%-2rem)] rounded-lg p-0 bg-transparent backdrop:bg-black/50"
      >
          <div className="bg-white rounded-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold">Invite friends to unlock AI</h3>
            <p className="text-sm text-gray-700">This feature costs us money. Please invite up to three friends who might benefit from Sport Fund. Sport Fund is free for athletes funding their sport.</p>
            <div className="space-y-2">
              {inviteEmails.map((val, idx) => (
                <input
                  key={idx}
                  type="email"
                  placeholder={`Friend ${idx + 1} email`}
                  value={val}
                  onChange={(e) => {
                    const next = [...inviteEmails];
                    next[idx] = e.target.value;
                    setInviteEmails(next);
                  }}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              ))}
              {inviteError && <p className="text-sm text-red-600">{inviteError}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2" onClick={() => setShowInviteModal(false)}>Cancel</button>
              <button
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
                onClick={async () => {
                  const filled = inviteEmails
                    .map(e => e.trim())
                    .filter(e => !!e && /.+@.+\..+/.test(e));
                  if (filled.length < 1) {
                    setInviteError('Please provide at least one valid email.');
                    return;
                  }
                  const normalized = filled.map(e => e.toLowerCase());
                  const uniqueCount = new Set(normalized).size;
                  if (uniqueCount !== normalized.length) {
                    setInviteError('Emails must be different.');
                    return;
                  }
                  setInviteError(null);
                  try {
                    await sendInviteEmails({ name: user?.displayName || undefined, email: user?.email || undefined, userId: user?.uid }, filled);
                    toast.success('Invitations sent!');
                    setShowInviteModal(false);
                    // Refresh invite count and unlock only if now >= 3
                    if (user?.uid) {
                      const n = await getInviteCountForUser(user.uid);
                      setInviteCount(n);
                      if (n >= 3) {
                        setShowUnlocked(true);
                      }
                    }
                  } catch (err: any) {
                    toast.error(err?.message || 'Failed to send invitations');
                  }
                }}
              >
                Send invites
              </button>
            </div>
          </div>
      </dialog>

      {/* Unlocked confirmation modal */}
      <dialog
        ref={unlockedDialogRef}
        onCancel={() => setShowUnlocked(false)}
        onClick={(e) => { if (e.target === unlockedDialogRef.current) setShowUnlocked(false); }}
        className="m-auto max-w-lg w-[calc(100%-2rem)] rounded-lg p-0 bg-transparent backdrop:bg-black/50"
      >
          <div className="bg-white rounded-lg w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold">AI features unlocked</h3>
            <p>Thank you for the invitations. We have unlocked your AI features. You can close this popup, add a draft of your post in the content field and click <strong>Generate with AI</strong>. This will create a more polished post.</p>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2" onClick={() => setShowUnlocked(false)}>Close</button>
            </div>
          </div>
      </dialog>

      {/* Fullscreen overlay loader during generation. No onCancel/onClick dismiss:
          there's nothing to cancel mid-flight, and this never had backdrop-dismiss
          behavior; it just needs top-layer stacking above any other open dialog. */}
      <dialog
        ref={generatingDialogRef}
        className="m-auto max-w-none rounded-md p-0 bg-transparent backdrop:bg-black/30"
      >
          <div className="bg-white p-6 rounded-md shadow-md flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            <span>Generating post with AI…</span>
          </div>
      </dialog>
    </>
  );
}